/*
  CART REQUEST GUARD.

  What this watches for, and what it deliberately no longer does.

  1. A cart call answered with an HTML challenge page is swallowed in silence.
     Under bot protection Shopify returns "verify your connection" instead of
     the cart, and every caller in the theme goes straight to .json(); the parse
     throws, a .catch writes to the console, and the shopper is left looking at
     a bag that no longer matches what the server holds. Hit twice while testing
     on 08/09/2026. The banner below says so where the shopper is looking.

  2. It USED to disable the checkout button for the length of every cart write,
     every stepper debounce and the whole Rebuy gift cycle. That is gone
     (09/09/2026). Between a named hold that never released, a stale flag that
     latched for the rest of the session, and a repaint racing the paint, the
     button spent too long greyed out with nothing on screen explaining why —
     reported from the bag as "it keeps loading forever". A checkout button
     that is occasionally a beat ahead of the server beats one that will not
     take a click at all.

     So: nothing here greys the button any more. Clicking checkout instead
     cancels every piece of cart work still outstanding — the pending stepper
     post, the "updating your bag" strip, every named hold — and lets the
     shopper straight through. Shopify re-reads the cart server-side on the
     checkout page, so the bag they pay for is the bag the server holds.

  3. Every cart write that lands is announced as 'cart:written' on document.
     saved-cart.js listens for it to save a signed-in shopper's bag, and it
     has to hear writes from apps too (Rebuy, the pop-up offer), which raise
     no theme event of their own. Only real answers count: an HTML challenge
     or a 4xx changed nothing worth saving.

  Rather than edit every caller, this wraps the two transports the theme uses
  for cart routes — window.fetch and XMLHttpRequest — so anything that talks to
  the cart is covered, including scripts loaded by apps.

  Deliberately NOT flagged as a failure: a 4xx that still carries a real body.
  /cart/change.js answers 422 when a line is unavailable and the theme handles
  it. Only an HTML reply means the bag on screen is untrustworthy.
*/
(function () {
  'use strict';

  // Reads and writes both signal a challenged session.
  var CART_ROUTE = /\/cart(\/|\.js)/;
  var CART_WRITE = /\/cart\/(add|change|update|clear)/;

  // Nothing may stay "busy" forever, even though busy no longer blocks anyone.
  var MAX_HOLD_MS = 20000;

  var inFlight = 0;
  var stale = false;
  var banner = null;

  /*
    Named windows the theme asks us to track that are invisible from the
    network alone — the pause between a stepper click and the POST it will
    send, and the Rebuy gift cycle the "updating your bag" strip covers, most
    of which is a markup GET rather than a cart write.

    These are bookkeeping only now: isHeld() reports them and a checkout click
    clears them. They no longer disable anything.
  */
  var holds = Object.create(null);
  var holdTimers = Object.create(null);

  function anyHold() {
    for (var name in holds) {
      if (holds[name]) return true;
    }
    return false;
  }

  function takeHold(name) {
    if (!name) return;
    if (holdTimers[name]) window.clearTimeout(holdTimers[name]);
    holds[name] = true;
    holdTimers[name] = window.setTimeout(function () { dropHold(name); }, MAX_HOLD_MS);
  }

  function dropHold(name) {
    if (!name || !holds[name]) return;
    delete holds[name];
    window.clearTimeout(holdTimers[name]);
    delete holdTimers[name];
  }

  function dropAllHolds() {
    for (var name in holds) dropHold(name);
  }

  /*
    Clears every trace of the old behaviour off a button.

    Shoppers carry cached pages, and the drawer re-renders from markup a
    previous version of this file may have stamped. Anything still wearing
    data-guard-held, aria-disabled, the inline opacity or a disabled flag the
    guard gave it is released here — with one exception.

    The pre-order restriction is a second, unrelated owner of this same button:
    it blocks checkout while the bag mixes pre-order and in-stock lines, which
    has nothing to do with a cart write finishing. It marks itself with
    data-preorder-held, so leave those alone and let that check release itself.
  */
  function unlock() {
    document.querySelectorAll('.cart__checkout, [name="checkout"]').forEach(function (el) {
      if (el.hasAttribute('data-guard-held')) {
        el.removeAttribute('data-guard-held');
        el.style.opacity = '';
        el.style.cursor = '';
      }

      if (el.hasAttribute('data-preorder-held')) return;

      el.removeAttribute('aria-disabled');
      if (el.disabled) el.disabled = false;
    });

    // Express buttons (Shop Pay, PayPal) are app-owned iframes an earlier
    // version blocked at the wrapper. Same story — hand them back.
    document.querySelectorAll('.additional-checkout-buttons').forEach(function (el) {
      if (el.style.pointerEvents === 'none') el.style.pointerEvents = '';
      if (el.style.opacity === '0.5') el.style.opacity = '';
    });
  }

  /*
    Checkout was clicked. Drop everything.

    Every hold goes, the in-flight count is zeroed, and the theme is told to
    abandon what it is still holding on to — the pending stepper POST, the
    "updating your bag" strip, any spinner left on a row. Listeners on a plain
    CustomEvent run synchronously, so all of that happens before the click's
    default action carries the shopper to /checkout.

    The stale banner is left standing on purpose: if a cart call really did come
    back as a challenge page, that is worth still being on screen.
  */
  function abort() {
    dropAllHolds();
    inFlight = 0;
    document.dispatchEvent(new CustomEvent('cart:abort'));
    unlock();
  }

  /*
    Capture phase, on the document, so this runs before the cart form's own
    submit handler and before anything an app has bound to the button. A click
    anywhere inside the checkout button counts — the label and any icon inside
    it are what actually receive the event.
  */
  document.addEventListener('click', function (evt) {
    var target = evt.target;
    if (!target || typeof target.closest !== 'function') return;
    if (!target.closest('.cart__checkout, [name="checkout"]')) return;
    abort();
  }, true);

  // A checkout reached by pressing Enter in the form never raises that click.
  document.addEventListener('submit', function (evt) {
    var form = evt.target;
    if (!form || typeof form.querySelector !== 'function') return;
    if (!form.querySelector('.cart__checkout, [name="checkout"]')) return;
    abort();
  }, true);

  function begin() {
    inFlight++;

    var done = false;
    var timer = window.setTimeout(function () { finish(); }, MAX_HOLD_MS);

    function finish() {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      if (inFlight > 0) inFlight--;
    }

    return finish;
  }

  /*
    The bag on screen can no longer be trusted. Say so where the shopper is
    already looking. Checkout stays open — the server holds whatever it holds
    and the checkout page reads it fresh — but they deserve to know the panel
    in front of them is out of date before they act on it.
  */
  function markStale() {
    if (stale) return;
    stale = true;
    showBanner();
  }

  function showBanner() {
    if (banner) { place(banner); return; }

    banner = document.createElement('div');
    banner.className = 'cart-guard-alert';
    banner.setAttribute('role', 'alert');
    banner.style.cssText = [
      'margin:12px 0', 'padding:12px 14px', 'border:1px solid #A32E22',
      'background:#FDF3F2', 'color:#7A241B',
      'border-radius:4px', 'font-size:14px', 'line-height:1.5',
      'display:flex', 'gap:12px', 'align-items:center', 'flex-wrap:wrap'
    ].join(';');

    var text = document.createElement('span');
    text.style.flex = '1 1 200px';
    text.textContent = 'We could not finish updating your bag, so what you see may be out of date. Please refresh before checking out.';

    var button = document.createElement('button');
    button.type = 'button';
    button.textContent = 'Refresh';
    button.style.cssText = 'padding:7px 14px;cursor:pointer;font:inherit;color:inherit;border:1px solid currentColor;background:transparent;border-radius:3px';
    button.addEventListener('click', function () { window.location.reload(); });

    banner.appendChild(text);
    banner.appendChild(button);
    place(banner);
  }

  /*
    Above the checkout button, in whichever bag is currently on screen. The
    drawer keeps its wrapper in the DOM while closed, so taking the first match
    dropped the message into a hidden 0x0 container and nobody saw it. Measure
    and take a visible one; re-running this moves the existing node rather than
    duplicating it.
  */
  function place(el) {
    var hosts = document.querySelectorAll('.cart__checkout-wrapper');
    var target = null;

    for (var i = 0; i < hosts.length; i++) {
      if (hosts[i].getBoundingClientRect().height > 0) { target = hosts[i]; break; }
    }
    if (!target && hosts.length) target = hosts[0];

    if (target && target.parentNode) {
      target.parentNode.insertBefore(el, target);
      return;
    }
    if (!el.parentNode) document.body.insertBefore(el, document.body.firstChild);
  }

  /*
    Shopify's cart routes answer "text/javascript" when healthy — not
    "application/json", which an earlier version of this file looked for and
    which flagged every good response as broken. The challenge page is the only
    thing that comes back as HTML, so that is what to test for.

    A 4xx carrying a real body is still a real answer: /cart/change.js returns
    422 for an unavailable line and the theme handles it.

    Status 0 used to count as a failure too. It does not any more: an XHR an app
    aborts, or one cancelled by the shopper navigating away, reports exactly the
    same 0, and those false alarms were putting the red banner up over a bag
    that was perfectly fine.
  */
  function replyIsUsable(contentType) {
    return (contentType || '').toLowerCase().indexOf('html') === -1;
  }

  // A write the server accepted. See point 3 at the top.
  function announceWrite(status, contentType) {
    if (status < 200 || status >= 400 || !replyIsUsable(contentType)) return;
    document.dispatchEvent(new CustomEvent('cart:written'));
  }

  /* ---- fetch ---- */

  var nativeFetch = window.fetch;

  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';

      if (!CART_ROUTE.test(url)) return nativeFetch.apply(this, arguments);

      var done = CART_WRITE.test(url) ? begin() : null;

      return nativeFetch.apply(this, arguments).then(function (response) {
        // Headers only — reading the body here would consume it for the caller.
        var type = response.headers.get('content-type');
        if (!replyIsUsable(type)) {
          markStale();
        }
        if (done) {
          done();
          announceWrite(response.status, type);
        }
        return response;
      }, function (error) {
        if (done) done();
        throw error;
      });
    };
  }

  /* ---- XMLHttpRequest ---- */

  var open = XMLHttpRequest.prototype.open;
  var send = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this.__cartGuardUrl = url || '';
    return open.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    var url = this.__cartGuardUrl || '';

    if (CART_ROUTE.test(url)) {
      var done = CART_WRITE.test(url) ? begin() : null;

      this.addEventListener('loadend', function () {
        if (done) done();
        var type = '';
        try { type = this.getResponseHeader('content-type') || ''; } catch (e) { /* headers unavailable */ }
        if (this.status !== 0 && !replyIsUsable(type)) markStale();
        if (done) announceWrite(this.status, type);
      });
    }

    return send.apply(this, arguments);
  };

  // The drawer and the cart page both re-render their markup. Re-place the
  // banner if one is up, and strip any leftover lock off the fresh button.
  ['cart:build', 'cart:updated', 'cart:open'].forEach(function (name) {
    document.addEventListener(name, function () {
      if (stale) showBanner();
      unlock();
    });
  });

  document.addEventListener('DOMContentLoaded', unlock);

  /*
    For the theme. hold()/release() are named so two overlapping windows do not
    release each other. refresh() is what a repaint calls once it has redrawn
    the button; it now only clears leftovers rather than re-applying a lock.
  */
  window.CartRequestGuard = {
    hold: takeHold,
    release: dropHold,
    refresh: unlock,
    abort: abort,

    isHeld: function () {
      return inFlight > 0 || anyHold();
    },

    isStale: function () {
      return stale;
    }
  };
}());
